import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import VerticalAlignCenterIcon from '@mui/icons-material/VerticalAlignCenter';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import LightTooltip from 'src/common/components/styled/LightTooltip';
import { ColumnAlignH, ColumnAlignV } from 'src/interfaces/PageColumn';

const ALIGN_H_OPTIONS: { value: ColumnAlignH; icon: ReactElement; labelKey: string }[] = [
  { value: 'left', icon: <FormatAlignLeftIcon fontSize="small" />, labelKey: 'left' },
  { value: 'center', icon: <FormatAlignCenterIcon fontSize="small" />, labelKey: 'center' },
  { value: 'right', icon: <FormatAlignRightIcon fontSize="small" />, labelKey: 'right' },
];

const ALIGN_V_OPTIONS: { value: ColumnAlignV; icon: ReactElement; labelKey: string }[] = [
  { value: 'top', icon: <VerticalAlignTopIcon fontSize="small" />, labelKey: 'top' },
  { value: 'center', icon: <VerticalAlignCenterIcon fontSize="small" />, labelKey: 'middle' },
  { value: 'bottom', icon: <VerticalAlignBottomIcon fontSize="small" />, labelKey: 'bottom' },
];

const toggleSx = {
  px: 0.75,
  py: 0.5,
  '&.Mui-selected': {
    borderColor: 'primary.main',
  },
  '&:not(.Mui-selected)': {
    color: 'text.secondary',
  },
} as const;

interface ColumnAlignControlsProps {
  alignH: ColumnAlignH;
  alignV: ColumnAlignV;
  onAlignHChange: (value: ColumnAlignH) => void;
  onAlignVChange: (value: ColumnAlignV) => void;
}

export default function ColumnAlignControls({
  alignH,
  alignV,
  onAlignHChange,
  onAlignVChange,
}: ColumnAlignControlsProps) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5,
      }}
      data-testid="column-align-controls"
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={alignH}
        aria-label={t('system.pages.hero.alignH')}
        onChange={(_, value) => {
          if (value) onAlignHChange(value as ColumnAlignH);
        }}
      >
        {ALIGN_H_OPTIONS.map(({ value, icon, labelKey }) => {
          const label = t(`system.pages.hero.align.${labelKey}`);
          return (
            <LightTooltip key={value} title={label} placement="top">
              <ToggleButton value={value} aria-label={label} sx={toggleSx}>
                {icon}
              </ToggleButton>
            </LightTooltip>
          );
        })}
      </ToggleButtonGroup>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={alignV}
        aria-label={t('system.pages.hero.alignV')}
        onChange={(_, value) => {
          if (value) onAlignVChange(value as ColumnAlignV);
        }}
      >
        {ALIGN_V_OPTIONS.map(({ value, icon, labelKey }) => {
          const label = t(`system.pages.hero.align.${labelKey}`);
          return (
            <LightTooltip key={value} title={label} placement="top">
              <ToggleButton value={value} aria-label={label} sx={toggleSx}>
                {icon}
              </ToggleButton>
            </LightTooltip>
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );
}
